import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../scripts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { resolveAvatarUrl } from "@/lib/avatarApi";
import {
  DEFAULT_COMMUNITY_GUIDELINES,
  communityBannerStyle,
  communityPageCssVars,
  communityThemeMode,
  resolveCommunityBrandColors,
} from "@/lib/communityTheme";
import { seedLikedFromPosts } from "@/lib/socialFeedLikes";
import {
  applyBrandCssVars,
  clearBrandCssVars,
  isBrandHex,
  normalizeBrandHexInput,
} from "@/lib/brandColors";
import { toastError, toastSuccess } from "@/lib/toast";
import { API_BASE_URL } from "@/lib/apiBase";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import { pathWithPostAnchor } from "@/lib/permalinks";
import { useSocialPaths } from "@/lib/socialPaths";
import { useSocialWriteApis } from "@/lib/socialWriteApis";
import {
  uploadSocialPostMedia,
  setSocialEmbed,
  uploadOrgSocialPostMedia,
  setOrgSocialEmbed,
} from "@/lib/socialMedia";
import { normalizeSocialFeedPost } from "@/lib/normalizeSocialFeedPost";
import CommunityMembersPanel from "./CommunityMembersPanel";
import CommunitySpaceEditForm from "./CommunitySpaceEditForm";
import PostComposer from "./PostComposer";
import PostCard from "./PostCard";
import VerifiedOrgMark from "./VerifiedOrgMark";
import { cn } from "@/lib/utils";

const visitorParams = { as_social_visitor: "true" };

/**
 * Public community page for Social visitors: /c/:communityId
 * No org privileges; join_policy applies to everyone.
 */
const SocialCommunityPage = () => {
  const { communityId } = useParams();
  const { accessToken, user } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = useSocialPaths();
  const writeApis = useSocialWriteApis();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [community, setCommunity] = useState(null);
  const [feed, setFeed] = useState([]);
  const [previewOnly, setPreviewOnly] = useState(false);
  const [posting, setPosting] = useState(false);
  const [composerKey, setComposerKey] = useState(0);
  const [likedPosts, setLikedPosts] = useState(() => new Set());
  const [descExpanded, setDescExpanded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    display_name: "",
    description: "",
    join_policy: "open",
    posting_policy: "official_only",
    brand_primary: "",
    brand_secondary: "",
    brand_accent: "",
    guidelines: "",
  });
  const pageRootRef = useRef(null);

  const openThread = useCallback(
    (post) => {
      const postId = post?.post_id ?? post?.social_post_id;
      if (postId == null) return;
      const returnTo = pathWithPostAnchor(
        `${location.pathname}${location.search || ""}`,
        postId
      );
      navigate(paths.thread({ postId, returnPath: returnTo }));
    },
    [navigate, location.pathname, location.search, paths]
  );

  const normalizePost = useCallback((p) => normalizeSocialFeedPost(p), []);

  const quoteEmbedRoot = (post) => {
    if (post?.repost_kind === "quote" && post.repost_of) return post.repost_of;
    if (
      post?.repost_kind === "bare" &&
      post.repost_of?.repost_kind === "quote" &&
      post.repost_of.repost_of
    ) {
      return post.repost_of.repost_of;
    }
    return null;
  };

  const load = useCallback(async () => {
    if (!accessToken || !communityId) return;
    setLoading(true);
    setError(null);
    try {
      const isNumericId = /^\d+$/.test(String(communityId));
      const orgBase = writeApis.actAsOrgId
        ? `${API_BASE_URL}/org/${encodeURIComponent(writeApis.actAsOrgId)}/social`
        : null;
      const detailUrl =
        orgBase && isNumericId
          ? `${orgBase}/communities/${communityId}`
          : isNumericId
            ? `${API_BASE_URL}/social/communities/${communityId}`
            : `${API_BASE_URL}/social/communities/by-slug/${encodeURIComponent(communityId)}`;
      const detailRes = await axios.get(detailUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: orgBase && isNumericId ? undefined : visitorParams,
      });
      const detail = detailRes.data;
      const resolvedId = detail?.community_id ?? detail?.communityId;
      if (!resolvedId) {
        throw new Error("Community not found");
      }
      setCommunity(detail);
      if (detail?.banned) {
        setFeed([]);
        setPreviewOnly(false);
        setDescExpanded(false);
        return;
      }
      const feedParams =
        orgBase && isNumericId ? undefined : visitorParams;
      const feedRes = await axios.get(
        `${API_BASE_URL}/social/communities/${resolvedId}/feed`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: feedParams,
        }
      );
      const posts = Array.isArray(feedRes.data?.posts)
        ? feedRes.data.posts.map(normalizePost)
        : [];
      setFeed(posts);
      setLikedPosts(seedLikedFromPosts(posts));
      setPreviewOnly(Boolean(feedRes.data?.preview_only));
      setDescExpanded(false);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load community"
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, communityId, normalizePost, writeApis.actAsOrgId]);

  useEffect(() => {
    load();
  }, [load]);

  const org = community?.org;
  const brandColors = useMemo(
    () => resolveCommunityBrandColors(community),
    [community]
  );
  const avatarSrc = resolveAvatarUrl(org?.avatar_url || null);
  const displayName = community?.display_name || org?.name || "Community";

  const bannerStyle = useMemo(
    () => communityBannerStyle(brandColors),
    [brandColors]
  );

  // Scope community brand tokens to the content inset (header + page), not the side nav.
  useEffect(() => {
    const pageEl = pageRootRef.current;
    if (!pageEl || !community) return undefined;
    const inset =
      pageEl.closest('[data-sidebar="inset"]') || pageEl;
    const mode = communityThemeMode(community);
    applyBrandCssVars(inset, communityPageCssVars(community));
    inset.setAttribute("data-community-theme", mode);
    return () => {
      clearBrandCssVars(inset);
      inset.removeAttribute("data-community-theme");
    };
  }, [community]);

  const guidelinesText =
    (community?.guidelines && String(community.guidelines).trim()) ||
    DEFAULT_COMMUNITY_GUIDELINES;

  const isCommunityModerator =
    community?.membership?.role === "moderator" &&
    community?.membership?.status === "active";
  const canManageCommunity =
    Boolean(community?.can_moderate) &&
    (!community?.owner_org_id ||
      Boolean(writeApis.isActAsOrg) ||
      isCommunityModerator);

  const handlePendingCountChange = useCallback((n) => {
    setCommunity((prev) => {
      if (!prev) return prev;
      const next = Number(n) || 0;
      if ((Number(prev.pending_member_count) || 0) === next) return prev;
      return { ...prev, pending_member_count: next };
    });
  }, []);

  const openSettings = () => {
    setSettingsForm({
      display_name: community?.display_name || "",
      description: community?.description || "",
      join_policy: community?.join_policy || "open",
      posting_policy: community?.posting_policy || "official_only",
      brand_primary: community?.brand_primary || "",
      brand_secondary: community?.brand_secondary || "",
      brand_accent: community?.brand_accent || "",
      guidelines: community?.guidelines || "",
    });
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    if (!community?.community_id || settingsBusy) return;
    for (const key of ["brand_primary", "brand_secondary", "brand_accent"]) {
      const raw = (settingsForm[key] || "").trim();
      if (raw && !isBrandHex(raw)) {
        toastError(`${key.replace("brand_", "")} must be #RRGGBB`);
        return;
      }
    }
    const name = (settingsForm.display_name || "").trim();
    if (!name || name.length < 2) {
      toastError("Name must be at least 2 characters");
      return;
    }
    setSettingsBusy(true);
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/social/communities/${community.community_id}`,
        {
          display_name: name,
          description: settingsForm.description,
          join_policy: settingsForm.join_policy,
          posting_policy: settingsForm.posting_policy,
          brand_primary: normalizeBrandHexInput(settingsForm.brand_primary),
          brand_secondary: normalizeBrandHexInput(settingsForm.brand_secondary),
          brand_accent: normalizeBrandHexInput(settingsForm.brand_accent),
          guidelines: settingsForm.guidelines?.trim()
            ? settingsForm.guidelines.trim()
            : null,
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: visitorParams,
        }
      );
      setCommunity((prev) => (prev ? { ...prev, ...res.data } : prev));
      setSettingsOpen(false);
      toastSuccess("Community settings saved");
    } catch (err) {
      toastError(
        err.response?.data?.message ||
          err.message ||
          "Could not save settings"
      );
    } finally {
      setSettingsBusy(false);
    }
  };

  const inviteOnly = community?.join_policy === "invite_only";
  const showJoin =
    !writeApis.isActAsOrg &&
    !inviteOnly &&
    (community?.access?.can_join || community?.access?.can_request);
  const ownedByActingOrg =
    writeApis.isActAsOrg &&
    community?.owner_org_id != null &&
    String(community.owner_org_id) === String(writeApis.actAsOrgId);
  const canComposePersonal =
    !writeApis.isActAsOrg &&
    Boolean(community?.can_post_as_self) &&
    !previewOnly;
  const canComposeAsOrg =
    writeApis.isActAsOrg &&
    writeApis.canPublish &&
    ownedByActingOrg &&
    !previewOnly;
  const canCompose = canComposePersonal || canComposeAsOrg;
  const membershipStatus = community?.membership?.status;

  const handleJoin = async () => {
    if (!community?.community_id) return;
    try {
      await axios.post(
        `${API_BASE_URL}/social/communities/${community.community_id}/join`,
        {},
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: visitorParams,
        }
      );
      toastSuccess(
        community.access?.can_request
          ? "Join request submitted"
          : "Joined community"
      );
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not join"
      );
    }
  };

  const handleLeave = async () => {
    if (!community?.community_id) return;
    const pending = membershipStatus === "pending";
    const ok = await confirm({
      title: pending ? "Cancel join request?" : "Leave community?",
      description: pending
        ? "Your pending request will be withdrawn."
        : "You will need to join again to post and see member-only content.",
      confirmLabel: pending ? "Cancel request" : "Leave",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.post(
        `${API_BASE_URL}/social/communities/${community.community_id}/leave`,
        {},
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: visitorParams,
        }
      );
      toastSuccess(pending ? "Request cancelled" : "Left community");
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not leave"
      );
    }
  };

  const handleComposerSaveDraft = async (payload) => {
    if (!community?.community_id || !canComposePersonal) return;
    setPosting(true);
    try {
      const { data: created } = await axios.post(
        `${API_BASE_URL}/social/communities/${community.community_id}/posts`,
        {
          title: payload.title,
          content: payload.content,
          as_org: false,
          is_draft: true,
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: visitorParams,
        }
      );
      const draftPostId =
        created?.social_post_id ?? created?.post_id ?? created?.postId;
      if (draftPostId != null && payload.files?.length) {
        try {
          for (const file of payload.files) {
            await uploadSocialPostMedia(accessToken, draftPostId, file);
          }
        } catch (mediaErr) {
          toastError(mediaErr, "Draft saved, but media upload failed");
        }
      }
      if (draftPostId != null && payload.embedUrl) {
        try {
          await setSocialEmbed(accessToken, draftPostId, payload.embedUrl);
        } catch (embedErr) {
          toastError(embedErr, "Draft saved, but video link failed");
        }
      }
      setComposerKey((k) => k + 1);
      toastSuccess("Draft saved");
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not save draft"
      );
      throw err;
    } finally {
      setPosting(false);
    }
  };

  const handleComposerSubmit = async (payload) => {
    if (!community?.community_id || !canCompose) return;
    setPosting(true);
    try {
      let created;
      if (canComposeAsOrg) {
        const res = await axios.post(
          writeApis.createPostUrl,
          {
            title: payload.title,
            content: payload.content,
            community_id: community.community_id,
          },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        created = res.data;
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/social/communities/${community.community_id}/posts`,
          {
            title: payload.title,
            content: payload.content,
            as_org: false,
          },
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: visitorParams,
          }
        );
        created = res.data;
      }
      const createdPostId =
        created?.social_post_id ?? created?.post_id ?? created?.postId;
      if (createdPostId != null && payload.files?.length) {
        try {
          for (const file of payload.files) {
            if (canComposeAsOrg && writeApis.actAsOrgId) {
              await uploadOrgSocialPostMedia(
                accessToken,
                writeApis.actAsOrgId,
                createdPostId,
                file
              );
            } else {
              await uploadSocialPostMedia(accessToken, createdPostId, file);
            }
          }
        } catch (mediaErr) {
          toastError(mediaErr, "Posted, but media upload failed");
        }
      }
      if (createdPostId != null && payload.embedUrl) {
        try {
          if (canComposeAsOrg && writeApis.actAsOrgId) {
            await setOrgSocialEmbed(
              accessToken,
              writeApis.actAsOrgId,
              createdPostId,
              payload.embedUrl
            );
          } else {
            await setSocialEmbed(accessToken, createdPostId, payload.embedUrl);
          }
        } catch (embedErr) {
          toastError(embedErr, "Posted, but video link failed");
        }
      }
      toastSuccess("Posted");
      setComposerKey((k) => k + 1);
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not post"
      );
      throw err;
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!accessToken) return;
    if (writeApis.isActAsOrg && !writeApis.allowEngage) return;
    const id = String(postId);
    const already = likedPosts.has(id);
    try {
      if (already) {
        if (writeApis.isActAsOrg) return;
        const list = await axios.get(
          `${API_BASE_URL}/social/posts/${id}/interactions`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const rows = Array.isArray(list.data) ? list.data : [];
        const mine = rows.find(
          (i) =>
            (i.interaction_type === "like" || i.interactionType === "like") &&
            !i.parent_id &&
            !i.parentId &&
            (i.user_id === user?.user_id || i.userId === user?.user_id)
        );
        if (mine) {
          await axios.delete(
            `${API_BASE_URL}/social/posts/interactions/${
              mine.interaction_id || mine.interactionId
            }`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
        }
        setLikedPosts((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setFeed((prev) =>
          prev.map((p) => {
            const pid = String(p.post_id);
            const rid = p.repost_of
              ? String(p.repost_of.social_post_id ?? p.repost_of.post_id)
              : null;
            if (pid === id) {
              return {
                ...p,
                like_count: Math.max(0, (p.like_count || 0) - 1),
              };
            }
            if (rid === id && p.repost_of) {
              return {
                ...p,
                repost_of: {
                  ...p.repost_of,
                  like_count: Math.max(0, (p.repost_of.like_count || 0) - 1),
                },
              };
            }
            return p;
          })
        );
        return;
      }

      await axios.post(
        writeApis.interactionsUrl(id),
        { interaction_type: "like" },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setLikedPosts((prev) => new Set(prev).add(id));
      setFeed((prev) =>
        prev.map((p) => {
          const pid = String(p.post_id);
          const rid = p.repost_of
            ? String(p.repost_of.social_post_id ?? p.repost_of.post_id)
            : null;
          if (pid === id) {
            return { ...p, like_count: (p.like_count || 0) + 1 };
          }
          if (rid === id && p.repost_of) {
            return {
              ...p,
              repost_of: {
                ...p.repost_of,
                like_count: (p.repost_of.like_count || 0) + 1,
              },
            };
          }
          return p;
        })
      );
    } catch {
      toastError("Could not update like");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (community?.banned) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center space-y-4">
        <h1 className="text-xl font-semibold text-foreground">
          You’re banned from this community
        </h1>
        <p className="text-sm text-muted-foreground">
          You can no longer view or participate in{" "}
          {community.display_name || "this community"}.
        </p>
        <Button asChild variant="outline">
          <Link to="/explore">Back to Explore</Link>
        </Button>
      </div>
    );
  }

  if (error || !community) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Alert variant="destructive">
          <AlertDescription>{error || "Community not found"}</AlertDescription>
        </Alert>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/explore">Back to Explore</Link>
        </Button>
      </div>
    );
  }

  return (
    <div
      ref={pageRootRef}
      className="min-h-full flex flex-col bg-background text-foreground"
    >
      <div
        className="relative w-full border-b border-border"
        style={bannerStyle}
      >
        <div className="mx-auto flex max-w-5xl min-w-0 flex-col gap-4 px-4 py-4 sm:py-10 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar
              className="h-16 w-16 shrink-0 border-2 bg-background/90 shadow-md"
              style={{ borderColor: brandColors.brand_accent }}
            >
              {avatarSrc ? <AvatarImage src={avatarSrc} alt="" /> : null}
              <AvatarFallback className="bg-background/80 text-lg text-foreground">
                {(displayName || "?").slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h1 className="flex max-w-full min-w-0 items-center gap-1.5 text-2xl font-semibold">
                <span className="truncate" title={displayName}>
                  {displayName}
                </span>
                <VerifiedOrgMark
                  org={community}
                  title="Verified community"
                  className="h-5 w-5 shrink-0"
                />
              </h1>
              {org?.name && org.name !== displayName ? (
                <p
                  className="mt-1 flex min-w-0 items-center gap-1 text-sm"
                  style={{ color: "var(--community-banner-muted)" }}
                >
                  <span className="truncate" title={org.name}>
                    {org.name}
                  </span>
                  <VerifiedOrgMark org={org} className="shrink-0" />
                </p>
              ) : null}
              <p
                className="mt-1.5 text-sm"
                style={{ color: "var(--community-banner-muted)" }}
              >
                {Number(community.member_count) || 0} members
                {" · "}
                {Number(community.active_member_count_30d) || 0} active in the
                last 30 days
              </p>
              {inviteOnly ? (
                <Badge className="mt-2 border-0 bg-background/25 text-inherit">
                  Invite only
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {canManageCommunity ? (
              <Button
                type="button"
                variant="outline"
                className="border-current/40 bg-background/20 text-inherit hover:bg-background/30 hover:text-inherit"
                onClick={openSettings}
              >
                Edit
              </Button>
            ) : null}
            {canManageCommunity ? (
              <CommunityMembersPanel
                communityId={community.community_id}
                accessToken={accessToken}
                canChangeRoles={Boolean(community.can_change_roles)}
                pendingCount={Number(community.pending_member_count) || 0}
                onPendingCountChange={handlePendingCountChange}
              />
            ) : null}
            {showJoin ? (
              <Button
                type="button"
                className="hover:opacity-90"
                style={{
                  backgroundColor: "var(--community-banner-cta-bg)",
                  color: "var(--community-banner-cta-fg)",
                }}
                onClick={handleJoin}
              >
                {community.access?.can_request ? "Request to join" : "Join"}
              </Button>
            ) : null}
            {membershipStatus === "pending" ? (
              <Button
                type="button"
                variant="outline"
                className="border-current/40 bg-background/20 text-inherit hover:bg-background/30 hover:text-inherit"
                onClick={handleLeave}
              >
                Cancel request
              </Button>
            ) : null}
            {membershipStatus === "active" ? (
              <Button
                type="button"
                variant="outline"
                className="border-current/40 bg-background/20 text-inherit hover:bg-background/30 hover:text-inherit"
                onClick={handleLeave}
              >
                Leave
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-6">
        {community.description ? (
          <div className="mb-6 max-w-3xl rounded-lg border border-border border-l-4 border-l-primary bg-card px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              About
            </p>
            <p
              className={cn(
                "mt-1 text-sm whitespace-pre-wrap",
                !descExpanded && "line-clamp-2"
              )}
            >
              {community.description}
            </p>
            {String(community.description).length > 120 ||
            String(community.description).includes("\n") ? (
              <button
                type="button"
                className="mt-1 min-h-10 px-1 text-sm font-medium text-primary hover:underline sm:min-h-0 sm:text-xs"
                onClick={() => setDescExpanded((v) => !v)}
              >
                {descExpanded ? "Show less" : "Show more"}
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="flex min-w-0 flex-col gap-8 xl:flex-row xl:items-start">
          <div className="min-w-0 flex-1 space-y-6 max-w-2xl">
            {inviteOnly && membershipStatus !== "active" ? (
              <Alert>
                <AlertDescription>
                  This community is invite only. You can browse the listing, but
                  joining requires an invitation.
                </AlertDescription>
              </Alert>
            ) : null}

            {previewOnly ? (
              <Alert>
                <AlertDescription>
                  Limited preview — join or request access to see the full feed.
                </AlertDescription>
              </Alert>
            ) : null}

            {canCompose ? (
              <div className="space-y-2 border-b border-border pb-6">
                {canComposeAsOrg ? (
                  <p className="text-sm text-muted-foreground">
                    Post as {writeApis.displayName || "organization"}
                  </p>
                ) : null}
                <PostComposer
                  mode="collapsed"
                  destinationMode="fixed"
                  hideDestination
                  hideTags
                  inferTitle={false}
                  fixedTeamId={community.community_id}
                  fixedTeamLabel={
                    canComposeAsOrg
                      ? writeApis.displayName || "Organization"
                      : "Community"
                  }
                  isSubmitting={posting}
                  onSubmit={handleComposerSubmit}
                  onSaveDraft={
                    canComposePersonal ? handleComposerSaveDraft : undefined
                  }
                  formKey={composerKey}
                  allowCritical={false}
                  submitLabel="Post"
                />
              </div>
            ) : null}

            <div>
              {feed.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No posts yet.
                </p>
              ) : (
                feed.map((post) => {
                  const postId = post.post_id;
                  return (
                    <PostCard
                      key={postId}
                      post={post}
                      onExpand={() =>
                        openThread(
                          post.repost_kind === "bare" && post.repost_of
                            ? post.repost_of
                            : post
                        )
                      }
                      likeCount={post.like_count || 0}
                      commentCount={post.reply_count || 0}
                      accessToken={accessToken}
                      onLike={() =>
                        handleLike(
                          post.repost_kind === "bare" && post.repost_of
                            ? post.repost_of.social_post_id ??
                                post.repost_of.post_id
                            : postId
                        )
                      }
                      onComment={() =>
                        openThread(
                          post.repost_kind === "bare" && post.repost_of
                            ? post.repost_of
                            : post
                        )
                      }
                      isLiked={likedPosts.has(
                        String(
                          post.repost_kind === "bare" && post.repost_of
                            ? post.repost_of.social_post_id ??
                                post.repost_of.post_id
                            : postId
                        )
                      )}
                      authorAboveTitle
                      relativeTimestamps
                      socialTombstone
                      socialRepost
                      onQuoteCreated={(created) => {
                        if (created)
                          setFeed((prev) => [normalizePost(created), ...prev]);
                      }}
                      onEmbedLike={() => {
                        const root = quoteEmbedRoot(post);
                        if (!root) return;
                        return handleLike(
                          root.social_post_id ?? root.post_id
                        );
                      }}
                      onEmbedExpand={() => {
                        const root = quoteEmbedRoot(post);
                        if (root) openThread(root);
                      }}
                      embedIsLiked={(() => {
                        const root = quoteEmbedRoot(post);
                        return root
                          ? likedPosts.has(
                              String(root.social_post_id ?? root.post_id)
                            )
                          : false;
                      })()}
                    />
                  );
                })
              )}
            </div>
          </div>

          <aside className="w-full min-w-0 shrink-0 space-y-5 xl:sticky xl:top-4 xl:w-64">
            <section className="rounded-lg border border-border border-t-4 border-t-primary bg-card px-4 py-3 space-y-2 shadow-sm overflow-hidden">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Guidelines
              </h2>
              <p className="text-sm whitespace-pre-wrap text-foreground/90">
                {guidelinesText}
              </p>
            </section>
            <section className="rounded-lg border border-border border-t-4 border-t-primary bg-card px-4 py-3 space-y-2 shadow-sm">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Community
              </h2>
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Members</dt>
                  <dd>{Number(community.member_count) || 0}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Active (30d)</dt>
                  <dd>{Number(community.active_member_count_30d) || 0}</dd>
                </div>
                <div className="flex justify-between gap-2 min-w-0">
                  <dt className="shrink-0 text-muted-foreground">Join</dt>
                  <dd className="min-w-0 truncate capitalize text-right">
                    {(community.join_policy || "open").replace(/_/g, " ")}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 min-w-0">
                  <dt className="shrink-0 text-muted-foreground">Posting</dt>
                  <dd className="min-w-0 truncate capitalize text-right">
                    {(community.posting_policy || "official_only").replace(
                      /_/g,
                      " "
                    )}
                  </dd>
                </div>
              </dl>
            </section>
          </aside>
        </div>
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit community</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <CommunitySpaceEditForm
              idPrefix="community-page-edit"
              disabled={settingsBusy}
              value={settingsForm}
              onChange={setSettingsForm}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSettingsOpen(false)}
              disabled={settingsBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveSettings}
              disabled={settingsBusy}
            >
              {settingsBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialCommunityPage;
